import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { LanguageSwitcher } from '../ui/language-switcher';
import { LoadingSpinner } from '../ui/loading';
import { useToast } from '../../hooks/use-toast';
import { api } from '../../lib/api';
import { ReactComponent as LurganLogo } from '../../assets/logos-svg/Lurgan-Horizontal-Vector_Lurgan-Logo-Dubh.svg';
import { ReactComponent as BainisteoirLogo } from '../../assets/logos-svg/Bainisteoir-Lurgan-Vector.svg';
import { PasswordStrengthChecklist } from './PasswordStrengthChecklist';

export function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [resetErrors, setResetErrors] = useState([]);

  const token = searchParams.get('token');

  const resetPasswordSchema = z.object({
    new_password: z.string()
      .min(12, t('verifyEmail.errors.passwordMin', 'Password must be at least 12 characters'))
      .regex(/[A-Z]/, t('verifyEmail.errors.passwordUppercase', 'Password must contain at least one uppercase letter'))
      .regex(/[a-z]/, t('verifyEmail.errors.passwordLowercase', 'Password must contain at least one lowercase letter'))
      .regex(/[0-9]/, t('verifyEmail.errors.passwordNumber', 'Password must contain at least one number'))
      .regex(/[!@#$%^&*(),.?":{}|<>]/, t('verifyEmail.errors.passwordSpecial', 'Password must contain at least one special character (!@#$%^&*)')),
    new_password_confirm: z.string()
      .min(1, t('verifyEmail.errors.confirmRequired', 'Please confirm your password')),
  }).refine((data) => data.new_password === data.new_password_confirm, {
    message: t('verifyEmail.errors.passwordMatch', 'Passwords do not match'),
    path: ['new_password_confirm'],
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { new_password: '', new_password_confirm: '' },
  });

  const passwordValue = watch('new_password', '');

  const extractErrors = (error) => {
    const data = error.response?.data;
    if (!data) return [t('verifyEmail.errors.default', 'Reset failed. Please try again.')];
    if (data.token) return [t('verifyEmail.errors.tokenInvalid', 'This link is no longer valid. Please contact the college to get a new one.')];
    if (data.error) return [data.error];
    if (data.detail) return [data.detail];
    if (data.non_field_errors) return Array.isArray(data.non_field_errors) ? data.non_field_errors : [data.non_field_errors];
    if (data.new_password) return Array.isArray(data.new_password) ? data.new_password : [data.new_password];
    if (data.password) return Array.isArray(data.password) ? data.password : [data.password];
    return [t('verifyEmail.errors.default', 'Reset failed. Please try again.')];
  };

  if (!token) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ background: 'linear-gradient(135deg, #F6BD66, #F28482, #F5CAC3)' }}>
        <LurganLogo className="h-14 mb-6 [&_path]:fill-black dark:[&_path]:fill-white" aria-label="Coláiste Lurgan" />
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-end mb-2">
              <LanguageSwitcher />
            </div>
            <BainisteoirLogo className="h-10 mx-auto mb-3 text-black dark:text-white" aria-label="Bainisteoir" />
            <CardTitle className="text-lg font-semibold text-destructive">
              {t('resetPassword.invalidToken')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-center text-muted-foreground mb-4">
              {t('resetPassword.invalidTokenDesc')}
            </p>
            <Button onClick={() => navigate('/forgot-password')} className="w-full">
              {t('forgotPassword.title')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const onSubmit = async (data) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setResetErrors([]);

    try {
      await api.auth.resetPassword(token, data.new_password, data.new_password_confirm);

      toast({
        title: t('resetPassword.successTitle'),
        description: t('resetPassword.successDesc'),
        variant: 'success',
      });

      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 2000);

    } catch (error) {
      setResetErrors(extractErrors(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ background: 'linear-gradient(135deg, #F6BD66, #F28482, #F5CAC3)' }}>
      <LurganLogo className="h-14 mb-6 [&_path]:fill-black dark:[&_path]:fill-white" aria-label="Coláiste Lurgan" />
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-end mb-2">
            <LanguageSwitcher />
          </div>
          <BainisteoirLogo className="h-10 mx-auto mb-3 text-black dark:text-white" aria-label="Bainisteoir" />
          <CardTitle className="text-lg font-semibold text-muted-foreground">
            {t('resetPassword.title')}
          </CardTitle>
          <CardDescription>
            {t('resetPassword.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" autoComplete="off">
            {/* New Password */}
            <div className="space-y-2">
              <Label htmlFor="new_password">{t('resetPassword.newPassword')}</Label>
              <div className="relative">
                <Input
                  id="new_password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  name="new-password"
                  {...register('new_password')}
                  className={errors.new_password ? 'border-destructive' : ''}
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground"
                  disabled={isSubmitting}
                  tabIndex={-1}
                >
                  {showPassword ? t('login.hide') : t('login.show')}
                </button>
              </div>
              {errors.new_password && (
                <p className="text-sm text-destructive">{errors.new_password.message}</p>
              )}
              <PasswordStrengthChecklist password={passwordValue} />
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="new_password_confirm">{t('resetPassword.confirmPassword')}</Label>
              <div className="relative">
                <Input
                  id="new_password_confirm"
                  type={showPasswordConfirm ? 'text' : 'password'}
                  autoComplete="new-password"
                  name="new-password-confirm"
                  {...register('new_password_confirm')}
                  className={errors.new_password_confirm ? 'border-destructive' : ''}
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground"
                  disabled={isSubmitting}
                  tabIndex={-1}
                >
                  {showPasswordConfirm ? t('login.hide') : t('login.show')}
                </button>
              </div>
              {errors.new_password_confirm && (
                <p className="text-sm text-destructive">{errors.new_password_confirm.message}</p>
              )}
            </div>

            {/* Errors */}
            {resetErrors.length > 0 && (
              <div className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
                {resetErrors.length === 1 ? (
                  <p>{resetErrors[0]}</p>
                ) : (
                  <ul className="list-disc list-inside space-y-1">
                    {resetErrors.map((err, i) => <li key={i}>{err}</li>)}
                  </ul>
                )}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <LoadingSpinner size="sm" className="border-primary-foreground" />
                  <span>{t('resetPassword.submit')}</span>
                </div>
              ) : (
                t('resetPassword.submit')
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default ResetPassword;
